import { get as getMobx, set as setMobx, isObservable } from 'mobx';
export function keysOf<T extends object>(obj: T): (keyof T)[] {
    return Object.keys(obj) as (keyof T)[];
}

export function get<T extends object, U extends keyof T>(obj: T, key: U extends string ? U : never): T[U] {
    return getMobx(obj, key) as T[U];
}

export function deepget<TObj, TKey extends DeepKeyOf<TObj>>(obj: TObj, key: TKey): DeepTypeForKey<TObj, TKey> {
    const arrMatch = (key as string).match(/^\[(\d+)\]\.?(.*)$/);
    if (arrMatch) {
        const arrIdx = Number(arrMatch[1]);
        const restKey = arrMatch[2] ?? '';

        const objAsArray = obj as unknown as unknown[];
        const target = objAsArray[arrIdx];
        if (restKey === '') {
            return target as DeepTypeForKey<TObj, TKey>;
        } else {
            if (target === undefined) return undefined as DeepTypeForKey<TObj, TKey>;
            return deepget(target, restKey as unknown as '');
        }
    }

    const propMatch = (key as string).match(/^([^.[]+)\.?(.*)$/);
    if (!propMatch) throw new Error(`tried to get invalided property name ${key as string}`);

    const prop = propMatch[1] ?? '';
    const restKey = propMatch[2] ?? '';
    const target = (
        isObservable(obj) ? getMobx(obj as object, prop) : (obj as unknown as Record<string, unknown>)[prop]
    ) as unknown;
    if (restKey === '') {
        return target as DeepTypeForKey<TObj, TKey>;
    } else {
        if (target === undefined) return undefined as DeepTypeForKey<TObj, TKey>;
        return deepget(target, restKey as unknown as '');
    }
}

export function deepset<TObj, TKey extends DeepKeyOf<TObj>>(obj: TObj, key: TKey, value: DeepTypeForKey<TObj, TKey>) {
    const arrMatch = (key as string).match(/^\[(\d+)\]\.?(.*)$/);
    if (arrMatch) {
        const arrIdx = Number(arrMatch[1]);
        const restKey = arrMatch[2] ?? '';
        const objAsArray = obj as unknown as unknown[];
        if (restKey === '') {
            objAsArray[arrIdx] = value;
        } else {
            const target = objAsArray[arrIdx];
            if (target === undefined)
                throw new Error(`tried to set property ${restKey} on undefined element of array [${arrIdx}]`);
            deepset(target, restKey as unknown as '', value as never);
        }
        return;
    }

    const propMatch = (key as string).match(/^([^.[]+)\.?(.*)$/);
    if (!propMatch) throw new Error(`tried to set invalided property name ${key as string}`);

    const prop = propMatch[1] ?? '';
    const restKey = propMatch[2] ?? '';
    const objAsRecord = obj as unknown as Record<string, unknown>;

    if (restKey === '') {
        if (isObservable(objAsRecord)) {
            setMobx(objAsRecord, prop, value);
        } else {
            objAsRecord[prop] = value;
        }
    } else {
        const target = objAsRecord[prop];
        if (target === undefined)
            throw new Error(`tried to set property ${restKey} on undefined element of object ${prop}`);
        deepset(target, restKey as unknown as '', value as never);
    }
}
